import { useCallback, useMemo } from 'react'
import type { NextRouter, PrefetchOptions, Url } from 'next/dist/shared/lib/router/router'
// eslint-disable-next-line no-restricted-imports
import { useRouter } from 'next/router'
import { logger } from '../logging'

/** Copied from next.js types. This interface is not publicly exported. */
export interface TransitionOptions {
  shallow?: boolean
  locale?: string | false
  scroll?: boolean
}

export type PushFn = (url: Url, as?: Url, options?: TransitionOptions) => Promise<boolean>

export function useSafeRouter(): NextRouter {
  const router = useRouter()

  const safePush = useCallback<PushFn>(
    (url, as, options) => {
      return router
        .push(url, as, options)
        .then((success) => {
          logger().debug('router push succeeded', { url, as, options, router })
          return success
        })
        .catch((err) => {
          logger().error('router push failed', { url, as, options, router }, err)
          return false
        })
    },
    [router],
  )

  const safeReplace = useCallback<PushFn>(
    (url, as, options) => {
      return router
        .replace(url, as, options)
        .then((success) => {
          logger().debug('router replace succeeded', { url, as, options, router })
          return success
        })
        .catch((err) => {
          logger().error('router replace failed', { url, as, options, router }, err)
          return false
        })
    },
    [router],
  )

  const safePrefetch = useCallback(
    (url: string, asPath?: string, options?: PrefetchOptions): Promise<void> => {
      return router
        .prefetch(url, asPath, options)
        .then(() => {
          logger().debug('router prefetch succeeded', { url, asPath, options, router })
        })
        .catch((err) => {
          logger().error('router prefetch failed', { url, asPath, options, router }, err)
        })
    },
    [router],
  )

  return useMemo(() => {
    return {
      ...router,
      prefetch: safePrefetch,
      push: safePush,
      replace: safeReplace,
    }
  }, [router, safePrefetch, safePush, safeReplace])
}
